import { useStore } from '@/store'
import {
  useCreateCustomerShoppingCartMutation,
  useActiveShoppingCartQuery,
  useUpdateShoppingCartLocationMutation,
  useCountriesQuery,
  SortEnumType,
} from '@/generated/graphql'
import { useRouter } from 'next/navigation'
import { tracking } from '@/tracking'
import { queryClient } from '@/lib/queryClient'
import { toastEmitter } from '@/General/Toast'
import { useCookie } from '@/Cookies/useCookies'
import { fetchData } from '@/lib/fetcher'
import { clearClientAuthSession } from '@/lib/auth/clientSession'
import { AUTH_API_PATHS } from '@/lib/auth/paths'
import { sanitizeRedirectPath } from '@/lib/auth/safeRedirect'
import { broadcastAuthTabSyncEvent } from '@/lib/auth/tabSync'
import { useImpersonationStatus } from '@/Auth/useImpersonationStatus'
import { useStorefrontMarket } from '@/useStorefrontMarket'
import { fetchWithCloudflarePreclearance } from '@/lib/cloudflarePreclearance'

type SignInContext =
  | 'checkout'
  | 'cart'
  | 'pdp'
  | 'header-login'
  | 'account-login'
  | 'signup'
  | 'unknown'

type SignInOptions = {
  skipCartMerge?: boolean
  redirectTo?: string | null
  context?: SignInContext
}

type MergeAnonymousShoppingCartResponse = {
  mergeAnonymousShoppingCart: {
    success: boolean
    status: string
    mergedItemCount: number
    skippedItemCount: number
    skippedReasonCodes: string[]
  }
}

type MergeAnonymousShoppingCartVariables = {
  anonymousCartToken: string
  customerId: number
  countryIsoCode: string
}

const CHECKOUT_LOGIN_SESSION_FLAG = 'vi_logged_in_during_checkout'

const mergeAnonymousShoppingCartDocument = `
  mutation MergeAnonymousShoppingCart(
    $anonymousCartToken: String!
    $customerId: Int!
    $countryIsoCode: String!
  ) {
    mergeAnonymousShoppingCart(
      anonymousCartToken: $anonymousCartToken
      customerId: $customerId
      countryIsoCode: $countryIsoCode
    ) {
      success
      status
      mergedItemCount
      skippedItemCount
      skippedReasonCodes
    }
  }
`

function getContextFromPathname(): SignInContext {
  if (typeof window === 'undefined') return 'unknown'
  return window.location.pathname.includes('/checkout') ? 'checkout' : 'unknown'
}

function resolveMergePolicy(
  options: SignInOptions | undefined,
  currentCartToken: string | null
): {
  shouldMerge: boolean
  reason:
    | 'explicit_skip'
    | 'checkout_context'
    | 'missing_cart_token'
    | 'eligible_non_checkout'
  context: SignInContext
} {
  const context = options?.context ?? getContextFromPathname()

  if (context === 'checkout') {
    return { shouldMerge: false, reason: 'checkout_context', context }
  }

  if (options?.skipCartMerge === true) {
    return { shouldMerge: false, reason: 'explicit_skip', context }
  }

  if (!currentCartToken || currentCartToken.length === 0) {
    return { shouldMerge: false, reason: 'missing_cart_token', context }
  }

  return { shouldMerge: true, reason: 'eligible_non_checkout', context }
}

export function useAuth() {
  const cachedCountry = useStore(state => state.country)
  const storefrontMarket = useStorefrontMarket({
    cachedCountryIsoCode: cachedCountry,
  })
  const isResolved = useStore(state => state.authResolved)
  const router = useRouter()
  const { setCookie } = useCookie()
  const { mutate: createCustomerShoppingCart } =
    useCreateCustomerShoppingCartMutation()
  const { mutateAsync: updateShoppingCartLocation } =
    useUpdateShoppingCartLocationMutation()
  const user = useStore(state => state.user)
  const { isImpersonatingCurrentUser } = useImpersonationStatus()

  const clearAuthSession = clearClientAuthSession

  const signIn = async (
    email: string,
    password: string,
    options?: SignInOptions
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      const currentCartToken = useStore.getState().shoppingCartId
      const mergePolicy = resolveMergePolicy(options, currentCartToken)
      const anonymousCartToken = mergePolicy.shouldMerge
        ? currentCartToken
        : null

      fetchWithCloudflarePreclearance(AUTH_API_PATHS.LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: email,
          password,
        }),
      })
        .then(async response => {
          const data = (await response.json().catch(() => null)) as {
            success: boolean
            error?: string | null
            user?: {
              id: number
              firstName: string
              lastName: string
              email: string
              exp: number
            } | null
          } | null

          if (!response.ok || !data?.success || !data.user) {
            throw new Error(data?.error || 'Authentication failed')
          }

          return data
        })
        .then(async data => {
          if (data.user) {
            tracking.identify(data.user.email, {
              customerId: data.user.id,
            })
            tracking.track('logged_in', { method: 'email' })
            if (mergePolicy.context === 'checkout') {
              tracking.track('Logged in during checkout')
              try {
                window.sessionStorage.setItem(CHECKOUT_LOGIN_SESSION_FLAG, '1')
              } catch {
                // ignore
              }
            }

            if (mergePolicy.reason === 'checkout_context') {
              tracking.track('Deferred checkout cart merge', {
                reason: mergePolicy.reason,
              })
            }

            const customerId = data.user.id

            useStore.getState().setUser({
              id: customerId,
              firstName: data.user.firstName,
              lastName: data.user.lastName,
              email: data.user.email,
              exp: data.user.exp,
            })

            // Ensure the browser has applied Set-Cookie from the login response before any
            // authenticated request (e.g. merge cart, create cart, or add-to-cart right after login).
            await new Promise(r => setTimeout(r, 150))
            broadcastAuthTabSyncEvent('customer', 'signin')

            // Merge anonymous cart if it exists (token is a non-empty string).
            const countryIso = storefrontMarket.countryIsoCode
            let mergeSucceeded = false
            let mergeStatus: string | null = null
            if (
              anonymousCartToken &&
              typeof anonymousCartToken === 'string' &&
              anonymousCartToken.length > 0
            ) {
              try {
                const mergeResult = await fetchData<
                  MergeAnonymousShoppingCartResponse,
                  MergeAnonymousShoppingCartVariables
                >(mergeAnonymousShoppingCartDocument, {
                  anonymousCartToken,
                  customerId,
                  countryIsoCode: countryIso,
                })()
                const response = mergeResult.mergeAnonymousShoppingCart
                mergeStatus = response.status

                if (response.success) {
                  mergeSucceeded = true
                  tracking.track('Executed cart merge outside checkout', {
                    status: response.status,
                    mergedItemCount: response.mergedItemCount,
                    skippedItemCount: response.skippedItemCount,
                  })
                  if (response.skippedItemCount > 0) {
                    tracking.track('Cart merge had partial skips', {
                      status: response.status,
                      skippedItemCount: response.skippedItemCount,
                      skippedReasonCodes: response.skippedReasonCodes,
                    })
                  }
                  if (response.mergedItemCount > 0) {
                    toastEmitter.emit('toast', {
                      title: 'Cart Updated',
                      body: 'Your cart has been updated with your saved items',
                      variant: 'success',
                      duration: 4000,
                    })
                  }
                } else {
                  console.error('Merge returned unsuccessful status', response)
                }
              } catch (error) {
                console.error('Failed to merge anonymous cart:', error)
                // Continue with login even if merge fails
              }
            }

            // If skipCartMerge is true, keep using the current cart (don't fetch customer cart)
            if (options?.skipCartMerge === true && currentCartToken) {
              useStore.getState().setShoppingCartId(currentCartToken)

              if (options?.redirectTo) {
                const safeRedirect = sanitizeRedirectPath(
                  options.redirectTo,
                  '/account/'
                )
                router.replace(safeRedirect)
              }
              resolve(true)
              return
            }

            // Fetch customer's cart using the same query key as useShoppingCart
            const customerCartKey = useActiveShoppingCartQuery.getKey({
              customerId,
            })
            let cartData
            try {
              cartData = await queryClient.fetchQuery({
                queryKey: customerCartKey,
                queryFn: () =>
                  useActiveShoppingCartQuery.fetcher({
                    customerId,
                  })(),
              })
            } catch (error) {
              console.error('Failed to fetch customer cart:', error)
              // If merge succeeded, cart SHOULD exist - this is a network error
              // Don't create duplicate cart, just log and continue
              if (mergeSucceeded) {
                console.error(
                  'Merge succeeded but fetch failed - network issue'
                )
              }
              cartData = { activeShoppingCart: null }
            }

            if (cartData.activeShoppingCart === null) {
              // Only create cart if merge didn't succeed (to avoid duplicates)
              // If merge succeeded, cart exists but fetch failed - skip creation
              if (!mergeSucceeded) {
                try {
                  await new Promise<void>((resolveCart, rejectCart) => {
                    createCustomerShoppingCart(
                      {
                        customerId,
                        countryIsoCode: countryIso,
                      },
                      {
                        onSuccess: async () => {
                          try {
                            useStore.getState().setShoppingCartId(null)

                            await queryClient.refetchQueries({
                              queryKey: ['ActiveShoppingCart'],
                            })

                            resolveCart()
                          } catch (err) {
                            rejectCart(err as Error)
                          }
                        },
                        onError: error => {
                          rejectCart(error as Error)
                        },
                      }
                    )
                  })
                } catch (error) {
                  console.error('Failed to create customer cart:', error)
                  // Continue with login even if cart creation fails
                }
              } else {
                // Merge succeeded but fetch returned null - likely network issue
                // Cart exists on server but we can't see it yet
                // Force a refetch to try again
                console.error(
                  'Cart should exist after merge - retrying fetch',
                  mergeStatus
                )
                await queryClient.refetchQueries({
                  queryKey: ['ActiveShoppingCart'],
                })
              }
            } else {
              const cart = cartData.activeShoppingCart

              // Update cart currency if user is browsing in different region
              if (cart && cart.country?.isoCode !== countryIso) {
                try {
                  await updateShoppingCartLocation({
                    shoppingCartToken: cart.token,
                    countryIsoCode: countryIso,
                  })

                  // CRITICAL: Update vi-brand cookie to match new country
                  // Fetch countries data only when needed during sign-in
                  const countriesData = await queryClient.fetchQuery({
                    queryKey: [
                      'Countries',
                      { order: { name: SortEnumType.Asc } },
                    ],
                    queryFn: () =>
                      useCountriesQuery.fetcher({
                        order: { name: SortEnumType.Asc },
                      })(),
                  })
                  const newCountry = countriesData?.countries.find(
                    c => c.isoCode === countryIso
                  )
                  if (newCountry?.brandCode) {
                    setCookie('vi-brand', newCountry.brandCode)
                  }
                } catch (error) {
                  console.error('Failed to update cart currency:', error)
                  // Continue with login even if currency update fails
                }
              }

              useStore.getState().setShoppingCartId(null)

              if (anonymousCartToken) {
                queryClient.removeQueries({
                  queryKey: useActiveShoppingCartQuery.getKey({
                    anonymousCartToken,
                  }),
                })
              }
              await queryClient.refetchQueries({
                queryKey: ['ActiveShoppingCart'],
              })
            }

            // Redirect to the page the user was trying to access
            const rawRedirectTarget =
              options?.redirectTo ??
              (() => {
                const params = new URLSearchParams(window.location.search)
                return params.get('redirect') || params.get('post_redirect')
              })()
            const redirectTarget = sanitizeRedirectPath(
              rawRedirectTarget,
              '/account/'
            )
            if (redirectTarget) {
              router.replace(redirectTarget)
            }
            // If no redirect parameter, don't redirect - stay on current page
            // This allows modals and other components with onLoginSuccess callbacks to handle the flow

            resolve(true) // Success
          }
        })
        .catch(error => {
          reject(error)
        })
    })
  }

  const signOut = async () => {
    const isImpersonating = isImpersonatingCurrentUser

    if (isImpersonating) {
      try {
        const stopResponse = await fetchWithCloudflarePreclearance(
          AUTH_API_PATHS.ADMIN_IMPERSONATE_SYNC,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )

        const stopResult = (await stopResponse.json().catch(() => null)) as {
          success?: boolean
          redirectUrl?: string | null
        } | null

        if (
          !stopResponse.ok ||
          !stopResult?.success ||
          !stopResult.redirectUrl
        ) {
          throw new Error('Failed to prepare impersonation cleanup')
        }

        clearAuthSession()
        broadcastAuthTabSyncEvent('customer', 'signout')
        window.location.assign(stopResult.redirectUrl)
        return
      } catch (error) {
        console.error('Error stopping impersonation during sign out:', error)
        toastEmitter.emit('toast', {
          title:
            'Could not confirm the impersonation session ended. Please try again.',
          variant: 'error',
        })
        return
      }
    }

    try {
      tracking.track('Logged out')

      const response = await fetchWithCloudflarePreclearance(
        AUTH_API_PATHS.SIGNOUT,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error(`Sign out failed: ${response.status}`)
      }

      const data = (await response.json().catch(() => null)) as {
        success: boolean
        logoutUrl?: string | null
      } | null
      if (data?.success) {
        clearAuthSession()
        broadcastAuthTabSyncEvent('customer', 'signout')

        if (data.logoutUrl) {
          window.location.assign(data.logoutUrl)
        } else {
          // Avoid sending customers to Keycloak without a usable id_token_hint; it may
          // show an extra hosted logout confirmation page.
          window.location.assign(window.location.origin)
        }
      }
    } catch (error) {
      console.error('Error signing out:', error)
      // Force logout locally even if server call fails
      clearAuthSession()
      broadcastAuthTabSyncEvent('customer', 'signout')
    }
  }

  return {
    isAuthenticated: !!user,
    isResolved,
    signIn,
    signOut,
  }
}
